import React, { useEffect } from 'react';
import { BoltIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useMigrationProgress } from '../hooks/useMigrationProgress';

interface Props {
  runId: string;
  targets: string[];
  onComplete: (result: { status: 'success' | 'failed'; failedTarget?: string; succeededTargets: string[]; errorMessage?: string }) => void;
}

const RunProgressPanel: React.FC<Props> = ({ runId, targets, onComplete }) => {
  const { status, currentStatement, errorByTarget, source, complete, succeededTargets, failedTarget, errorMessage, ceilingReached, acknowledgeCeiling } =
    useMigrationProgress(runId, targets);

  useEffect(() => {
    if (complete) {
      onComplete({ status: complete.status, failedTarget, succeededTargets, errorMessage });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complete]);

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
      <div className="px-5 py-3 border-b border-slate-200 text-sm font-medium text-slate-700 flex items-center gap-2">
        <span>Run progress · <span className="font-mono text-xs text-slate-500">{runId}</span></span>
        <span className="ml-auto inline-flex items-center gap-1 text-xs text-slate-400">
          {source === 'sse' && (<><BoltIcon className="w-3.5 h-3.5" /> Live</>)}
          {source === 'polling' && (<><ArrowPathIcon className="w-3.5 h-3.5 animate-pulse" /> Polling…</>)}
        </span>
      </div>
      <ul className="divide-y divide-slate-100">
        {targets.map((t) => {
          const s = status[t];
          return (
            <li key={t} className="flex items-center gap-3 px-5 py-2 text-sm">
              <span className={`inline-block w-2.5 h-2.5 rounded-full ${
                s === 'success' ? 'bg-emerald-500' :
                s === 'failed' ? 'bg-red-500' :
                s === 'running' ? 'bg-blue-500 animate-pulse' : 'bg-slate-300'
              }`} />
              <span className="font-mono text-slate-800">{t}</span>
              <span className="text-xs text-slate-500 ml-auto">
                {s === 'running' && currentStatement[t] != null && <>statement #{currentStatement[t] + 1}</>}
                {s === 'failed' && errorByTarget[t] && <span className="text-red-600">{errorByTarget[t]}</span>}
                {s === 'success' && 'completed'}
                {s === 'pending' && 'queued'}
              </span>
            </li>
          );
        })}
      </ul>
      {ceilingReached && (
        <div className="px-5 py-3 border-t border-slate-200 bg-amber-50 text-xs text-amber-800 flex items-center gap-2">
          <span>This run has been polling for 5 minutes. Still waiting?</span>
          <button
            onClick={acknowledgeCeiling}
            className="ml-auto px-2 py-1 text-amber-800 bg-white border border-amber-200 rounded hover:bg-amber-100"
          >
            Continue
          </button>
        </div>
      )}
    </div>
  );
};

export default RunProgressPanel;
